import { useState, useEffect, useCallback } from 'react';
import { Organization, Team, Member, Subscription } from '../types';
import { ApiError } from '../utils/error-handler';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';
import { extractErrorMessage } from '../utils/error-handler';
import { useOrganization } from '../context/OrganizationContext';

interface TeamOverview {
  team: Team;
  organization?: Organization;
  userRole?: string;
  members: Member[];
  permissions: string[];
  subscription?: any;
  roles: any[];
}

export function useTeamOverview(teamId: string | undefined) {
  const { refreshData } = useOrganization(); // Get refresh method from context
  const [overview, setOverview] = useState<TeamOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchOverview = useCallback(async () => {
    if (!teamId) {
      setIsLoading(false);
      setOverview(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setOverview(null);

    try {
      console.log(`Fetching comprehensive team data for: ${teamId}`);
      
      // Fetch team details using new endpoint
      const team = await teamService.getTeam(teamId);
      
      // Fetch team roles
      const roles = await teamService.getTeamRoles(teamId, true);
      
      // Note: Team subscription info is now fetched via organization subscriptions
      // The useTeamSubscription hook handles this separately
      let subscription = null;
      
      // Get organization details if available
      let organization = null;
      const orgId = (team as any).organization_id || team.organizationId;
      if (orgId) {
        try {
          organization = await organizationService.getOrganizationById(orgId);
        } catch (orgError) {
          console.warn('Could not fetch organization details:', orgError);
        }
      }
      
      // Debug log to see what we're getting
      console.log('Team data from API:', team);
      
      // Transform response to match our interface
      const overviewData: TeamOverview = {
        team: {
          ...team,
          _id: (team as any).id || team._id || '',
          id: (team as any).id || team._id || '',
          organizationId: (team as any).organization_id || team.organizationId || '',
          organizationName: organization?.name || 'Unknown Organization',
          createdAt: typeof team.createdAt === 'string' ? team.createdAt : team.createdAt?.toISOString() || new Date().toISOString(),
          updatedAt: typeof team.updatedAt === 'string' ? team.updatedAt : team.updatedAt?.toISOString() || new Date().toISOString()
        } as Team,
        organization: organization || undefined,
        userRole: undefined, // Will be determined from roles/permissions if needed
        members: [], // Will be loaded separately by useTeamMembers
        permissions: [], // Could be extracted from roles if needed
        subscription: subscription,
        roles
      };
      
      console.log('Team overview data:', overviewData.team);
      
      setOverview(overviewData);
      setError(null);
    } catch (err) {
      console.error(`Error fetching team overview for ${teamId}:`, err);
      setError(extractErrorMessage(err));
      setOverview(null);
    } finally {
      setIsLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const updateTeam = async (data: { name?: string; description?: string }) => {
    if (!teamId || !overview) {
      return {
        success: false,
        error: { code: 'INVALID_REQUEST', message: 'No team loaded', status: 400 }
      };
    }

    try {
      setIsLoading(true);
      const updated = await teamService.updateTeam(teamId, data);

      // Refresh global context data (updates team name everywhere, including sidebar)
      await refreshData();

      // Update the local overview with the new team data
      setOverview(prev => prev ? {
        ...prev,
        team: {
          ...prev.team,
          ...updated,
          organizationId: (updated as any).organization_id || updated.organizationId || '',
          organizationName: prev.organization?.name || 'Unknown Organization',
          createdAt: typeof updated.createdAt === 'string' ? updated.createdAt : updated.createdAt?.toISOString() || prev.team.createdAt,
          updatedAt: typeof updated.updatedAt === 'string' ? updated.updatedAt : updated.updatedAt?.toISOString() || new Date().toISOString()
        }
      } : null);

      return { success: true, data: updated };
    } catch (err) {
      const error = extractErrorMessage(err);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTeam = async () => {
    if (!teamId) {
      return {
        success: false,
        error: { code: 'INVALID_REQUEST', message: 'No team ID', status: 400 }
      };
    }

    try {
      setIsLoading(true);
      await teamService.deleteTeam(teamId);

      // Refresh the global organization list in OrganizationContext
      // This ensures the team is removed from the organization's team list
      await refreshData();

      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    overview,
    team: overview?.team || null,
    organization: overview?.organization || null,
    userRole: overview?.userRole,
    members: overview?.members || [],
    permissions: overview?.permissions || [],
    subscription: overview?.subscription,
    roles: overview?.roles || [],
    isLoading,
    error,
    refreshOverview: fetchOverview,
    updateTeam,
    deleteTeam
  };
}

export default useTeamOverview;